import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Modal, Form, Spinner, Badge, Tab, Tabs, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../utils/permissionUtils';
import { countries, getStatesForCountry, getOrgAddressDefaults, matchIndianState } from '../utils/countriesStates';
import { saveBlobResponse } from '../utils/download';
import { loadOrgAddressDefaults } from '../utils/orgAddressDefaults';
import { formatCustomerDisplayName } from '../utils/customerDisplayName';
import { formatFulfillmentMethod } from '../utils/fulfillmentMethod';
import OrgPrintChrome from './OrgPrintChrome';
import LocationSelect from './LocationSelect';
import GstinLookupInput, { GSTIN_PATTERN } from './GstinLookupInput';
import PhoneNumberInput, { isValidPhoneNumber } from './PhoneNumberInput';
import BillCustomerPicker from './BillCustomerPicker';
import SaleNoteModal from './SaleNoteModal';
import WhatsAppShareModal from './WhatsAppShareModal';
import CatalogShareModal from './CatalogShareModal';
import { ProductThumb } from './ProductPhotos';
import Quotations from './Quotations';
import SalesReports from './SalesReports';
import TaxInvoice from './TaxInvoice';
import SaleNotes from './SaleNotes';
import { GstRegistrationBadges, GstRegistrationModal, GstRegistrationPrintBlock, useGstEnabled, useGstSummaries } from './GstRegistration';
import useSectionTab from '../hooks/useSectionTab';
import useLastRates from '../hooks/useLastRates';
import LastRateHint from './LastRateHint';
import PriceLists from './PriceLists';
import { todayISO } from '../utils/dates';
import HistoryButton from './HistoryButton';

import DateInput from './DateInput';
import ModuleAlerts from './ModuleAlerts';
import { formatDate } from '../utils/dateFormat';
/** Axios/API may return a non-array on error or proxy responses; keep list UIs safe. */
function toArray(data) {
  return Array.isArray(data) ? data : [];
}

// Section keys, in tab order; the first is where a first visit opens
const SALES_SECTIONS = ['customers', 'catalog', 'quotations', 'orders', 'salebills', 'notes', 'reports'];

function Sales() {
  const { user, priceListsEnabled, orgSettingsLoaded, storeEnabled } = useAuth();
  // Sending a bill on WhatsApp: whoever makes bills, and finance, who chase payment
  const canShareBills = !!user?.isSystemAdmin
    || hasAnyRole(user, ['ORG_ADMIN', 'MANAGER', 'USER', 'SALES_OPR', 'SALES_MGR', 'FIN_OPR', 'FIN_MGR']);
  const [shareBill, setShareBill] = useState(null);
  const [storeLinkCustomer, setStoreLinkCustomer] = useState(null); // a business customer's private store link being made
  // The product catalog sent to customers as a link (Sales > Product Catalog), and each product's main photo
  const canShareCatalog = !!user?.isSystemAdmin || hasAnyRole(user, ['ORG_ADMIN', 'MANAGER', 'USER', 'SALES_OPR', 'SALES_MGR']);
  const [shareCatalog, setShareCatalog] = useState(false);
  const [mainPhotos, setMainPhotos] = useState({});
  const [mainPhotoUrls, setMainPhotoUrls] = useState({});
  // Sales and profit reports show margins: the Org Admin and the sales and finance managers
  const canSeeReports = hasAnyRole(user, ['ORG_ADMIN', 'SALES_MGR', 'FIN_MGR']);
  const [loading, setLoading] = useState(true);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [organizationCurrency, setOrganizationCurrency] = useState('USD');
  const [orgAddressDefaults, setOrgAddressDefaults] = useState({ country: '', state: '', countryCode: '' });

  // The open section is in the address (/sales/<section>)
  // Price Lists is a section only where the organization has switched them on, for the people who keep them
  const isPriceListKeeper = hasAnyRole(user, ['ORG_ADMIN', 'SALES_MGR']);
  const canManagePriceLists = priceListsEnabled && isPriceListKeeper;
  // Until the organization's settings have loaded the address /sales/price-lists is kept, so a bookmark or a reload doesn't bounce to Customers
  const salesSections = isPriceListKeeper && (priceListsEnabled || !orgSettingsLoaded) ? [...SALES_SECTIONS.slice(0, 2), 'price-lists', ...SALES_SECTIONS.slice(2)] : SALES_SECTIONS;
  const [activeTab, setActiveTab] = useSectionTab('/sales', salesSections);

  // Customers state
  const [customers, setCustomers] = useState([]);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [customerFormData, setCustomerFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    companyName: '',
    customerType: 'INDIVIDUAL',
    address: '',
    city: '',
    district: '',
    state: '',
    country: '',
    postalCode: '',
    taxId: '',
    gstRegistrationType: '',
    creditDays: '',
    creditLimit: '',
    paymentTerms: '',
    priceListId: ''
  });
  const [priceLists, setPriceLists] = useState([]);
  // The names for the customer form; kept fresh when the open section changes (a list may have been added in Price Lists)
  useEffect(() => {
    if (!priceListsEnabled || !user?.organizationId) { setPriceLists([]); return; }
    axios.get('/api/price-lists').then((r) => setPriceLists(r.data?.lists || [])).catch(() => setPriceLists([]));
  }, [priceListsEnabled, user?.organizationId, activeTab]);
  const [selectedCustomerCountryCode, setSelectedCustomerCountryCode] = useState('');

  // Orders state
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [retailStores, setRetailStores] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orderFormData, setOrderFormData] = useState({
    orderNumber: '',
    customerId: '',
    orderStatus: 'PENDING',
    paymentStatus: 'PENDING',
    fulfillmentMethod: 'DELIVERY',
    retailStoreId: '',
    warehouseId: '',
    notes: ''
  });
  
  // Order items state
  const [orderItems, setOrderItems] = useState([]);
  const [newOrderItem, setNewOrderItem] = useState({
    productId: '',
    quantity: 1,
    unitPrice: '',
    discountPercentage: '',
    discountAmount: ''
  });
  /** Which discount input the user last committed to: % or $ (drives recalculation when qty/price/product changes). */
  const [draftDiscountLead, setDraftDiscountLead] = useState(null);

  // Sale Bill state
  const [saleBills, setSaleBills] = useState([]);
  const [noteDialog, setNoteDialog] = useState(null); // { bill, type } while issuing a credit/debit note
  const [notesRefreshKey, setNotesRefreshKey] = useState(0);
  const [creditWarning, setCreditWarning] = useState(null); // the credit-limit warning shown when issuing a bill
  const [gstDialog, setGstDialog] = useState(null); // bill whose IRN / e-way bill is being handled
  const gstEnabled = useGstEnabled(activeTab === 'salebills');
  const [gstSummaries, reloadGstSummaries] = useGstSummaries('SALE_BILL', toArray(saleBills).filter((b) => b.status === 'ISSUED').map((b) => b.id), activeTab === 'salebills');
  const [productionUnits, setProductionUnits] = useState([]);
  const [taxRates, setTaxRates] = useState([]);
  const [outboundMovementTypes, setOutboundMovementTypes] = useState([]);
  const [showSaleBillModal, setShowSaleBillModal] = useState(false);
  const [editingSaleBill, setEditingSaleBill] = useState(null);
  const [saleBillFormData, setSaleBillFormData] = useState({
    billDate: todayISO(),
    salesOrderId: '',
    customerId: '',
    movementTypeId: '',
    issuingLocationType: 'WAREHOUSE',
    issuingLocationId: '',
    paymentMethod: '',
    dueDate: '',
    notes: ''
  });
  const [saleBillLineItems, setSaleBillLineItems] = useState([]);
  // Sale Bill customer choice: 'details' (pick/create a customer) or 'walkin' (no details captured)
  const CUSTOMER_MODE_KEY = 'saleBillCustomerMode';
  const [billCustomerMode, setBillCustomerMode] = useState('details');
  const [walkInCustomerId, setWalkInCustomerId] = useState(null);

  // With a single Sale Bill movement type there is nothing to choose - use it automatically.
  const onlyMovementTypeId = outboundMovementTypes.length === 1 ? String(outboundMovementTypes[0].id) : null;
  useEffect(() => {
    if (showSaleBillModal && onlyMovementTypeId && !saleBillFormData.movementTypeId) {
      setSaleBillFormData((prev) => ({ ...prev, movementTypeId: onlyMovementTypeId }));
    }
  }, [showSaleBillModal, onlyMovementTypeId, saleBillFormData.movementTypeId]);

  // Likewise, if there is exactly one location the bill can issue from (after applying the movement
  // type's location-type restriction), select it by default. Mirrors what LocationSelect offers.
  useEffect(() => {
    if (!showSaleBillModal || editingSaleBill || saleBillFormData.issuingLocationId) return;
    const restrictTo = outboundMovementTypes.find((m) => String(m.id) === String(saleBillFormData.movementTypeId))?.locationType || null;
    const candidates = [
      ...(!restrictTo || restrictTo === 'WAREHOUSE' ? toArray(warehouses).filter((w) => w.isActive !== false).map((w) => ({ type: 'WAREHOUSE', id: w.id })) : []),
      ...(!restrictTo || restrictTo === 'RETAIL_STORE' ? toArray(retailStores).filter((r) => r.isActive !== false).map((r) => ({ type: 'RETAIL_STORE', id: r.id })) : []),
      ...(!restrictTo || restrictTo === 'PRODUCTION_UNIT' ? toArray(productionUnits).filter((u) => u.isActive !== false).map((u) => ({ type: 'PRODUCTION_UNIT', id: u.id })) : [])
    ];
    if (candidates.length === 1) {
      setSaleBillFormData((prev) => ({ ...prev, issuingLocationType: candidates[0].type, issuingLocationId: String(candidates[0].id) }));
    }
  }, [showSaleBillModal, editingSaleBill, saleBillFormData.issuingLocationId, saleBillFormData.movementTypeId,
      outboundMovementTypes, warehouses, retailStores, productionUnits]);

  // Customer fields state
  const [customerFields, setCustomerFields] = useState({
    phoneNumber: '',
    email: '',
    firstName: '',
    lastName: ''
  });

  // Help pane state

  // Print view state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  // The quotation a sale bill or order is being made from: recorded on it once that is saved
  const [quoteSource, setQuoteSource] = useState(null);
  const [quotesReloadKey, setQuotesReloadKey] = useState(0);
  const [printType, setPrintType] = useState(null);

  useEffect(() => {
    if (activeTab === 'customers') {
      fetchCustomers();
    } else if (activeTab === 'orders') {
      if (user?.organizationId) {
        fetchOrganizationCurrency();
      }
      fetchOrders();
      fetchCustomers(); // Need customers for order form
      fetchProducts(false);
      fetchRetailStores();
      fetchWarehouses();
    } else if (activeTab === 'reports') {
      if (user?.organizationId) {
        fetchOrganizationCurrency();
      }
    } else if (activeTab === 'quotations') {
      if (user?.organizationId) {
        fetchOrganizationCurrency();
      }
      fetchCustomers();
      fetchProducts(false);
      fetchTaxRates();
    } else if (activeTab === 'catalog') {
      if (user?.organizationId) {
        fetchOrganizationCurrency();
      }
      fetchProducts(true);
      axios.get('/api/product-images/main').then((r) => setMainPhotos(r.data || {})).catch(() => setMainPhotos({}));
      axios.get('/api/product-images/main-urls').then((r) => setMainPhotoUrls(r.data || {})).catch(() => setMainPhotoUrls({}));
    } else if (activeTab === 'salebills') {
      if (user?.organizationId) {
        fetchOrganizationCurrency();
      }
      fetchSaleBills();
      fetchOrders();
      fetchCustomers();
      fetchProducts(false);
      fetchRetailStores();
      fetchWarehouses();
      fetchProductionUnits();
      fetchTaxRates();
      fetchOutboundMovementTypes();
    }
  }, [activeTab, user?.organizationId]);

  useEffect(() => {
    if (!showSaleBillModal && !showOrderModal) setQuoteSource(null);
  }, [showSaleBillModal, showOrderModal]);

  // A bill made from a quotation arrives with its products filled in: load each line's lots for the issuing location
  const quoteLotsLoaded = useRef(null);
  useEffect(() => {
    if (!showSaleBillModal || quoteSource?.kind !== 'SALE_BILL' || !saleBillFormData.issuingLocationId) return;
    const key = `${quoteSource.id}:${saleBillFormData.issuingLocationType}:${saleBillFormData.issuingLocationId}`;
    if (quoteLotsLoaded.current === key) return;
    quoteLotsLoaded.current = key;
    saleBillLineItems.forEach((li, idx) => {
      if (li.productId) refreshSaleBillLotOptions(idx, li.productId, saleBillFormData.issuingLocationType, saleBillFormData.issuingLocationId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSaleBillModal, quoteSource, saleBillFormData.issuingLocationType, saleBillFormData.issuingLocationId]);

  const fetchOrganizationCurrency = async () => {
    try {
      if (user?.organizationId) {
        const response = await axios.get(`/api/organizations/${user.organizationId}`);
        const currency = response.data?.defaultCurrency || 'USD';
        setOrganizationCurrency(currency);
        setOrgAddressDefaults(getOrgAddressDefaults(response.data));
      }
    } catch (error) {
      console.error('Error fetching organization currency:', error);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') {
      return `${organizationCurrency} 0.00`;
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (Number.isNaN(numAmount)) {
      return `${organizationCurrency} 0.00`;
    }
    return `${organizationCurrency} ${numAmount.toFixed(2)}`;
  };

  // Customers functions
  const fetchCustomers = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await axios.get('/api/customers');
      setCustomers(toArray(response.data));
    } catch (error) {
      setCustomers([]);
      toast.error('Error fetching customers');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleCustomerInputChange = (e) => {
    setCustomerFormData({
      ...customerFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleGstinResolved = (result) => {
    // A GSTIN is India-only, so country is unambiguous here and this also unlocks the state dropdown.
    setSelectedCustomerCountryCode('IN');
    const address = [result.buildingNo, result.street, result.locality].filter(Boolean).join(', ');
    setCustomerFormData((prev) => ({
      ...prev,
      companyName: prev.companyName || result.legalName || result.tradeName || prev.companyName,
      address: address || prev.address,
      city: result.city || prev.city,
      district: result.district || prev.district,
      postalCode: result.pincode || prev.postalCode,
      country: 'India',
      state: matchIndianState(result.state) || prev.state,
      gstRegistrationType: prev.gstRegistrationType && prev.gstRegistrationType !== 'UNREGISTERED' ? prev.gstRegistrationType : 'REGULAR'
    }));
  };

  const handleCustomerSubmit = async (e) => {
    e.preventDefault();
    const taxId = (customerFormData.taxId || '').trim();
    if (selectedCustomerCountryCode === 'IN' && taxId && !GSTIN_PATTERN.test(taxId.toUpperCase())) {
      toast.error('GSTIN doesn\'t look valid - fix it or clear the field before saving');
      return;
    }
    if (customerFormData.phoneNumber && !isValidPhoneNumber(customerFormData.phoneNumber)) {
      toast.error('Phone number doesn\'t look valid');
      return;
    }
    const customerPayload = {
      ...customerFormData,
      creditDays: customerFormData.creditDays === '' ? null : parseInt(customerFormData.creditDays, 10),
      creditLimit: customerFormData.creditLimit === '' ? null : parseFloat(customerFormData.creditLimit),
      paymentTerms: customerFormData.paymentTerms || null,
      priceListId: customerFormData.priceListId === '' ? null : Number(customerFormData.priceListId)
    };
    try {
      if (editingCustomer) {
        await axios.put(`/api/customers/${editingCustomer.id}`, customerPayload);
        toast.success('Customer updated successfully');
      } else {
        await axios.post('/api/customers', customerPayload);
        toast.success('Customer created successfully');
      }
      setShowCustomerModal(false);
      setEditingCustomer(null);
      resetCustomerForm();
      fetchCustomers();
    } catch (error) {
      const errorMessage = error.response?.data || 'Error saving customer';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Error saving customer');
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setCustomerFormData({
      firstName: customer.firstName ?? '',
      lastName: customer.lastName ?? '',
      email: customer.email,
      phoneNumber: customer.phoneNumber,
      companyName: customer.companyName,
      customerType: customer.customerType,
      address: customer.address,
      city: customer.city,
      district: customer.district,
      state: customer.state,
      country: customer.country,
      postalCode: customer.postalCode,
      taxId: customer.taxId ?? '',
      gstRegistrationType: customer.gstRegistrationType ?? '',
      creditDays: customer.creditDays ?? '',
      creditLimit: customer.creditLimit ?? '',
      paymentTerms: customer.paymentTerms ?? '',
      priceListId: customer.priceListId ?? ''
    });
    const countryName = customer.country || '';
    const countryObj = countries.find((c) => c.name === countryName);
    setSelectedCustomerCountryCode(countryObj ? countryObj.code : '');
    setShowCustomerModal(true);
  };

  const handleDeleteCustomer = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await axios.delete(`/api/customers/${id}`);
        toast.success('Customer deleted successfully');
        fetchCustomers();
      } catch (error) {
        toast.error('Error deleting customer');
      }
    }
  };

  const resetCustomerForm = async () => {
    let defaults = orgAddressDefaults;
    if (!defaults.country && !defaults.state) {
      defaults = await loadOrgAddressDefaults(user?.organizationId);
      setOrgAddressDefaults(defaults);
    }
    setCustomerFormData({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      companyName: '',
      customerType: 'INDIVIDUAL',
      address: '',
      city: '',
      district: '',
      state: defaults.state,
      country: defaults.country,
      postalCode: '',
      taxId: '',
      gstRegistrationType: '',
      creditDays: '',
      creditLimit: '',
      paymentTerms: '',
      priceListId: ''
    });
    setSelectedCustomerCountryCode(defaults.countryCode);
  };

  const handleCloseCustomerModal = () => {
    setShowCustomerModal(false);
    setEditingCustomer(null);
    resetCustomerForm();
  };

  const handleExportSalesRegister = async () => {
    try {
      const response = await axios.get('/api/sale-bills/export', { responseType: 'blob' });
      saveBlobResponse(response, 'sales-register.csv');
    } catch (error) {
      toast.error('Failed to export the sales register');
    }
  };

  const handleViewPrint = (data, type) => {
    setPrintData(data);
    setPrintType(type);
    setShowPrintModal(true);
  };

  const handlePrint = () => {
    window.print();
  };

  // Orders functions
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/orders');
      setOrders(toArray(response.data));
    } catch (error) {
      setOrders([]);
      toast.error('Error fetching orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchRetailStores = async () => {
    try {
      const response = await axios.get('/api/inventory/retail-stores');
      const list = toArray(response.data).filter((s) => s.isActive !== false);
      setRetailStores(list);
    } catch (error) {
      setRetailStores([]);
      console.error('Error fetching retail stores:', error);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get('/api/inventory/warehouses');
      const list = toArray(response.data).filter((w) => w.isActive !== false);
      setWarehouses(list);
    } catch (error) {
      setWarehouses([]);
      console.error('Error fetching warehouses:', error);
    }
  };

  const fetchProductionUnits = async () => {
    try {
      const response = await axios.get('/api/inventory/production-units/active');
      setProductionUnits(toArray(response.data));
    } catch (error) {
      setProductionUnits([]);
      console.error('Error fetching production units:', error);
    }
  };

  const fetchTaxRates = async () => {
    try {
      const response = await axios.get('/api/inventory/tax-rates');
      setTaxRates(toArray(response.data).filter((t) => t.isActive !== false));
    } catch (error) {
      setTaxRates([]);
      console.error('Error fetching tax rates:', error);
    }
  };

  const fetchOutboundMovementTypes = async () => {
    try {
      const response = await axios.get('/api/inventory/goods-movement-types/direction/OUTBOUND');
      setOutboundMovementTypes(toArray(response.data).filter((mt) => mt.isActive !== false && mt.referenceType === 'SALES_BILL'));
    } catch (error) {
      setOutboundMovementTypes([]);
      console.error('Error fetching movement types:', error);
    }
  };

  const fetchSaleBills = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/sale-bills');
      setSaleBills(toArray(response.data));
    } catch (error) {
      setSaleBills([]);
      console.error('Error fetching sale bills:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLotsForSaleBillLine = async (productId, locationType, locationId) => {
    const product = products.find((p) => String(p.id) === String(productId));
    if (!product || !product.item?.id || !locationType || !locationId) {
      return [];
    }
    try {
      const response = await axios.get('/api/inventory/inventory-lots', {
        params: { locationType, locationId, itemId: product.item.id }
      });
      return toArray(response.data);
    } catch (error) {
      console.error('Error fetching inventory lots:', error);
      return [];
    }
  };

  const resetSaleBillForm = () => {
    setSaleBillFormData({
      billDate: todayISO(),
      salesOrderId: '',
      customerId: '',
      movementTypeId: '',
      issuingLocationType: 'WAREHOUSE',
      issuingLocationId: '',
      paymentMethod: '',
      dueDate: '',
      notes: ''
    });
    setSaleBillLineItems([]);
  };

  const ensureWalkInCustomerId = async () => {
    if (walkInCustomerId) return walkInCustomerId;
    const response = await axios.post('/api/customers/walk-in');
    setWalkInCustomerId(response.data.id);
    return response.data.id;
  };

  const handleBillCustomerModeChange = async (mode) => {
    if (mode === billCustomerMode) return;
    try {
      localStorage.setItem(CUSTOMER_MODE_KEY, mode);
    } catch (e) {
      // Preference just won't persist.
    }
    if (mode === 'walkin') {
      try {
        const id = await ensureWalkInCustomerId();
        setBillCustomerMode('walkin');
        setSaleBillFormData((prev) => ({ ...prev, customerId: String(id), salesOrderId: '' }));
      } catch (error) {
        toast.error('Could not set up the walk-in customer');
      }
    } else {
      setBillCustomerMode('details');
      setSaleBillFormData((prev) => ({ ...prev, customerId: '' }));
    }
  };

  const handleCreateSaleBill = async () => {
    setEditingSaleBill(null);
    resetSaleBillForm();
    let remembered = 'details';
    try {
      remembered = localStorage.getItem(CUSTOMER_MODE_KEY) === 'walkin' ? 'walkin' : 'details';
    } catch (e) {
      // Fall back to the default mode.
    }
    setBillCustomerMode('details');
    setShowSaleBillModal(true);
    if (remembered === 'walkin') {
      try {
        const id = await ensureWalkInCustomerId();
        setBillCustomerMode('walkin');
        setSaleBillFormData((prev) => ({ ...prev, customerId: String(id) }));
      } catch (error) {
        // Stay in details mode; the user can pick walk-in again.
      }
    }
  };

  /** Records on the quotation the sale bill or order made from it. */
  const linkQuote = async (source, targetId) => {
    try {
      await axios.post(`/api/quotations/${source.id}/converted`, { kind: source.kind, targetId });
      setQuotesReloadKey((k) => k + 1);
    } catch (error) {
      toast.warn(`Saved, but quotation ${source.number} wasn't marked: ${typeof error.response?.data === 'string' ? error.response.data : 'try again from the quotation'}`);
    }
    setQuoteSource(null);
  };

  /** Opens the sale bill form filled in from a quotation: the customer, products, quantities, rates, discounts and GST. */
  const makeBillFromQuote = (quote) => {
    setEditingSaleBill(null);
    resetSaleBillForm();
    setBillCustomerMode('details');
    setQuoteSource({ id: quote.id, number: quote.quoteNumber, kind: 'SALE_BILL' });
    setSaleBillFormData((prev) => ({ ...prev, customerId: String(quote.customer.id), notes: `Against quotation ${quote.quoteNumber}` }));
    setSaleBillLineItems(quote.lines.map((l) => ({
      productId: String(l.productId),
      quantity: Number(l.quantity),
      unitPrice: Number(l.unitPrice),
      discountPercentage: l.discountPercent != null ? Number(l.discountPercent) : '',
      discountAmount: '',
      taxRateId: l.taxRateId ? String(l.taxRateId) : '',
      grnLineItemId: '',
      lotOptions: []
    })));
    setActiveTab('salebills');
    setShowSaleBillModal(true);
    toast.info('Choose where the goods go from and the lots, then save the bill.');
  };

  /** Opens the sale order form filled in from a quotation. */
  const makeOrderFromQuote = (quote) => {
    const customer = customers.find((c) => String(c.id) === String(quote.customer.id));
    setEditingOrder(null);
    resetOrderForm();
    setQuoteSource({ id: quote.id, number: quote.quoteNumber, kind: 'ORDER' });
    setOrderFormData((prev) => ({ ...prev, customerId: quote.customer.id, notes: `Against quotation ${quote.quoteNumber}` }));
    // The order form wants +91… numbers; an older record may hold 10 digits. The customer is already chosen, so an
    // unusable number is left out rather than blocking the save.
    const rawPhone = (customer?.phoneNumber || quote.customer.phone || '').trim();
    const digits = rawPhone.replace(/\D/g, '');
    const phone = rawPhone.startsWith('+') ? rawPhone : digits.length === 10 ? `+91${digits}` : digits.length === 12 && digits.startsWith('91') ? `+${digits}` : '';
    setCustomerFields({
      phoneNumber: phone && isValidPhoneNumber(phone) ? phone : '',
      email: customer?.email || '',
      firstName: customer?.firstName || quote.customer.name || '',
      lastName: customer?.lastName || ''
    });
    setOrderItems(quote.lines.map((l) => ({
      productId: l.productId,
      productName: l.description,
      quantity: Number(l.quantity),
      unitPrice: Number(l.unitPrice),
      discountPercentage: l.discountPercent != null ? Number(l.discountPercent) : '',
      discountAmount: '',
      totalAmount: Number(l.taxable),
      discountLead: null
    })));
    if (quote.lines.some((l) => !Number.isInteger(Number(l.quantity)))) {
      toast.warn('Sale orders take whole quantities: check the quantities before saving.');
    }
    setActiveTab('orders');
    setShowOrderModal(true);
  };

  const handleCloseSaleBillModal = () => {
    setShowSaleBillModal(false);
    setEditingSaleBill(null);
    resetSaleBillForm();
  };

  const handleEditSaleBill = (bill) => {
    setEditingSaleBill(bill);
    setBillCustomerMode(bill.customer?.isWalkIn ? 'walkin' : 'details');
    if (bill.customer?.isWalkIn) setWalkInCustomerId(bill.customer.id);
    setSaleBillFormData({
      billDate: bill.billDate || todayISO(),
      salesOrderId: bill.salesOrder?.id ? String(bill.salesOrder.id) : '',
      customerId: bill.customer?.id ? String(bill.customer.id) : '',
      movementTypeId: bill.goodsMovementType?.id ? String(bill.goodsMovementType.id) : '',
      issuingLocationType: bill.issuingLocationType || 'WAREHOUSE',
      issuingLocationId: String(bill.warehouse?.id || bill.retailStore?.id || bill.sourceProductionUnit?.id || ''),
      paymentMethod: bill.paymentMethod || '',
      dueDate: bill.dueDate || '',
      notes: bill.notes || ''
    });
    setSaleBillLineItems((bill.lineItems || []).map((li) => ({
      productId: li.product?.id ? String(li.product.id) : '',
      quantity: li.quantity,
      unitPrice: li.unitPrice,
      discountPercentage: li.discountPercentage ?? '',
      discountAmount: li.discountAmount ?? '',
      taxRateId: li.taxRate?.id ? String(li.taxRate.id) : '',
      grnLineItemId: li.grnLineItem?.id ? String(li.grnLineItem.id) : '',
      unitOfMeasurementId: li.unitOfMeasurement?.id || null,
      lotOptions: li.grnLineItem ? [li.grnLineItem] : []
    })));
    setShowSaleBillModal(true);
  };

  const handleDeleteSaleBill = async (id) => {
    if (!window.confirm('Are you sure you want to delete this DRAFT Sale Bill?')) {
      return;
    }
    try {
      await axios.delete(`/api/sale-bills/${id}`);
      toast.success('Sale Bill deleted successfully');
      fetchSaleBills();
    } catch (error) {
      const errorMsg = error.response?.data || 'Error deleting Sale Bill';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error deleting Sale Bill');
    }
  };

  const handleSaleOrderSelectForBill = (orderId) => {
    const order = orders.find((o) => String(o.id) === String(orderId));
    if (order?.customer?.id) setBillCustomerMode('details');
    setSaleBillFormData((prev) => ({
      ...prev,
      salesOrderId: orderId,
      customerId: order?.customer?.id ? String(order.customer.id) : prev.customerId,
      issuingLocationType: order ? (order.fulfillmentMethod === 'PICKUP' ? 'RETAIL_STORE' : 'WAREHOUSE') : prev.issuingLocationType,
      issuingLocationId: order
        ? String((order.fulfillmentMethod === 'PICKUP' ? order.retailStore?.id : order.warehouse?.id) || '')
        : prev.issuingLocationId
    }));
  };

  const handleIssuingLocationChangeForBill = (value) => {
    const [locType, id] = value ? value.split(':') : ['WAREHOUSE', ''];
    const normalizedType = (locType || 'WAREHOUSE').toUpperCase();
    setSaleBillFormData((prev) => ({ ...prev, issuingLocationType: normalizedType, issuingLocationId: id }));
    saleBillLineItems.forEach((li, idx) => {
      if (li.productId) {
        refreshSaleBillLotOptions(idx, li.productId, normalizedType, id);
      }
    });
    setSaleBillLineItems((prev) => prev.map((li) => ({ ...li, grnLineItemId: '', lotOptions: [] })));
  };

  const refreshSaleBillLotOptions = async (index, productId, locationType, locationId) => {
    const lots = await fetchLotsForSaleBillLine(productId, locationType, locationId);
    setSaleBillLineItems((prev) => prev.map((li, i) => (i === index ? { ...li, lotOptions: lots, grnLineItemId: '' } : li)));
  };

  const handleAddSaleBillLine = () => {
    setSaleBillLineItems((prev) => [...prev, { productId: '', quantity: 1, unitPrice: '', discountPercentage: '', discountAmount: '', taxRateId: '', grnLineItemId: '', lotOptions: [] }]);
  };

  const handleRemoveSaleBillLine = (index) => {
    setSaleBillLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  // What this customer was last charged for each product (or quoted, if never billed)
  const saleBillRates = useLastRates('customers', showSaleBillModal ? saleBillFormData.customerId : null);
  const orderRates = useLastRates('customers', showOrderModal ? orderFormData.customerId : null);

  const applyLastSaleBillRate = (index) => {
    const last = saleBillRates[saleBillLineItems[index]?.productId];
    if (!last) return;
    setSaleBillLineItems((prev) => prev.map((li, i) => (i === index
      ? { ...li, unitPrice: String(Number(last.rate)), discountPercentage: last.discountPercent ? String(Number(last.discountPercent)) : '', discountAmount: '' }
      : li)));
  };

  const handleSaleBillLineChange = (index, field, value) => {
    setSaleBillLineItems((prev) => prev.map((li, i) => {
      if (i !== index) return li;
      const updated = { ...li, [field]: value };
      if (field === 'productId') {
        const product = products.find((p) => String(p.id) === String(value));
        updated.unitPrice = product?.price != null ? String(product.price) : updated.unitPrice;
        // A repeat customer starts from the rate (and discount) they were last charged
        const last = saleBillRates[value];
        if (last?.rate != null) {
          updated.unitPrice = String(Number(last.rate));
          updated.discountPercentage = last.discountPercent ? String(Number(last.discountPercent)) : '';
          updated.discountAmount = '';
        }
        // The item's usual GST rate (from Materials) picks the matching tax rate; it can still be changed
        if (product?.defaultGstRate != null) {
          const rate = taxRates.find((t) => t.isActive !== false && Number(t.ratePercent) === Number(product.defaultGstRate));
          if (rate) updated.taxRateId = String(rate.id);
        }
        updated.grnLineItemId = '';
        updated.lotOptions = [];
      }
      return updated;
    }));
    if (field === 'productId' && value) {
      refreshSaleBillLotOptions(index, value, saleBillFormData.issuingLocationType, saleBillFormData.issuingLocationId);
    }
  };

  const isSaleBillInterState = () => {
    const customer = customers.find((c) => String(c.id) === String(saleBillFormData.customerId));
    const customerState = (customer?.state || '').trim().toLowerCase();
    const orgState = (orgAddressDefaults?.state || '').trim().toLowerCase();
    if (!customerState || !orgState) return false;
    return customerState !== orgState;
  };

  const calculateSaleBillLineTax = (line) => {
    const qty = parseFloat(line.quantity) || 0;
    const price = parseFloat(line.unitPrice) || 0;
    const lineSubtotal = qty * price;
    let discount = 0;
    if (line.discountAmount) {
      discount = parseFloat(line.discountAmount) || 0;
    } else if (line.discountPercentage) {
      discount = lineSubtotal * (parseFloat(line.discountPercentage) / 100);
    }
    const taxable = Math.max(0, lineSubtotal - discount);
    const taxRate = taxRates.find((t) => String(t.id) === String(line.taxRateId));
    const ratePercent = taxRate ? parseFloat(taxRate.ratePercent) : 0;
    let cgst = 0, sgst = 0, igst = 0;
    if (isSaleBillInterState()) {
      igst = taxable * ratePercent / 100;
    } else {
      cgst = taxable * (ratePercent / 2) / 100;
      sgst = cgst;
    }
    const lineTotal = taxable + cgst + sgst + igst;
    return { lineSubtotal, discount, taxable, cgst, sgst, igst, lineTotal };
  };

  const calculateSaleBillTotals = () => {
    return saleBillLineItems.reduce((acc, line) => {
      const t = calculateSaleBillLineTax(line);
      return {
        subtotal: acc.subtotal + t.lineSubtotal,
        discount: acc.discount + t.discount,
        taxable: acc.taxable + t.taxable,
        cgst: acc.cgst + t.cgst,
        sgst: acc.sgst + t.sgst,
        igst: acc.igst + t.igst,
        total: acc.total + t.lineTotal
      };
    }, { subtotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 });
  };

  const buildSaleBillPayload = (status) => ({
    billDate: saleBillFormData.billDate,
    status,
    customer: { id: parseInt(saleBillFormData.customerId, 10) },
    salesOrder: saleBillFormData.salesOrderId ? { id: parseInt(saleBillFormData.salesOrderId, 10) } : null,
    goodsMovementType: saleBillFormData.movementTypeId ? { id: parseInt(saleBillFormData.movementTypeId, 10) } : null,
    issuingLocationType: saleBillFormData.issuingLocationType,
    warehouse: saleBillFormData.issuingLocationType === 'WAREHOUSE' && saleBillFormData.issuingLocationId
      ? { id: parseInt(saleBillFormData.issuingLocationId, 10) } : null,
    retailStore: saleBillFormData.issuingLocationType === 'RETAIL_STORE' && saleBillFormData.issuingLocationId
      ? { id: parseInt(saleBillFormData.issuingLocationId, 10) } : null,
    sourceProductionUnit: saleBillFormData.issuingLocationType === 'PRODUCTION_UNIT' && saleBillFormData.issuingLocationId
      ? { id: parseInt(saleBillFormData.issuingLocationId, 10) } : null,
    paymentMethod: saleBillFormData.paymentMethod || null,
    dueDate: saleBillFormData.dueDate || null,
    notes: saleBillFormData.notes || null,
    lineItems: saleBillLineItems.map((li) => ({
      product: { id: parseInt(li.productId, 10) },
      quantity: parseFloat(li.quantity),
      unitPrice: parseFloat(li.unitPrice),
      discountPercentage: li.discountPercentage ? parseFloat(li.discountPercentage) : null,
      discountAmount: li.discountAmount ? parseFloat(li.discountAmount) : null,
      taxRate: li.taxRateId ? { id: parseInt(li.taxRateId, 10) } : null,
      grnLineItem: li.grnLineItemId ? { id: parseInt(li.grnLineItemId, 10) } : null,
      unitOfMeasurement: li.unitOfMeasurementId ? { id: li.unitOfMeasurementId } : null
    }))
  });

  const handleSaveSaleBill = async (status, creditOverrideReason) => {
    if (!saleBillFormData.customerId) {
      toast.error('Please select a customer');
      return;
    }
    if (saleBillLineItems.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }
    if (status === 'ISSUED' && !saleBillFormData.movementTypeId) {
      toast.error('Please select a movement type to issue this bill');
      return;
    }
    if (status === 'ISSUED' && saleBillLineItems.some((li) => !li.grnLineItemId)) {
      toast.error('Please select an inventory lot for every line item before issuing');
      return;
    }
    try {
      const payload = buildSaleBillPayload(status);
      if (editingSaleBill) {
        await axios.put(`/api/sale-bills/${editingSaleBill.id}`, payload, { params: creditOverrideReason ? { creditOverrideReason } : undefined });
        toast.success(status === 'ISSUED' ? 'Sale Bill issued successfully' : 'Sale Bill updated successfully');
      } else {
        const { data: created } = await axios.post('/api/sale-bills', payload, { params: creditOverrideReason ? { creditOverrideReason } : undefined });
        toast.success(status === 'ISSUED' ? 'Sale Bill issued successfully' : 'Sale Bill saved as draft');
        if (quoteSource?.kind === 'SALE_BILL' && created?.id) await linkQuote(quoteSource, created.id);
      }
      setCreditWarning(null);
      handleCloseSaleBillModal();
      fetchSaleBills();
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 409 && data?.code === 'CREDIT_LIMIT_EXCEEDED') {
        setCreditWarning({ ...data, status, reason: '' });
        return;
      }
      const message = typeof data === 'string' ? data : data?.message;
      toast.error(message || 'Error saving Sale Bill');
    }
  };

  const formatFulfillmentLocation = (order) => {
    if (!order) return '—';
    if (order.fulfillmentMethod === 'PICKUP') {
      return order.retailStore?.name ? `Pick-up: ${order.retailStore.name}` : '—';
    }
    return order.warehouse?.name ? `Delivery: ${order.warehouse.name}` : '—';
  };

  const buildOrderPayload = (resolvedCustomerId, orderItemsData) => {
    const isPickup = orderFormData.fulfillmentMethod === 'PICKUP';
    return {
      orderNumber: orderFormData.orderNumber,
      orderStatus: orderFormData.orderStatus,
      paymentStatus: orderFormData.paymentStatus,
      fulfillmentMethod: orderFormData.fulfillmentMethod,
      notes: orderFormData.notes,
      customer: { id: resolvedCustomerId },
      retailStore:
        isPickup && orderFormData.retailStoreId
          ? { id: parseInt(String(orderFormData.retailStoreId), 10) }
          : null,
      warehouse:
        !isPickup && orderFormData.warehouseId
          ? { id: parseInt(String(orderFormData.warehouseId), 10) }
          : null,
      orderItems: orderItemsData
    };
  };

  const fetchProducts = async (forCatalogTab = false) => {
    try {
      if (forCatalogTab) setCatalogLoading(true);
      const response = await axios.get('/api/products');
      setProducts(toArray(response.data));
    } catch (error) {
      setProducts([]);
      toast.error('Error fetching products');
    } finally {
      if (forCatalogTab) setCatalogLoading(false);
    }
  };

  const handleOrderInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'fulfillmentMethod') {
      setOrderFormData({
        ...orderFormData,
        fulfillmentMethod: value,
        retailStoreId: '',
        warehouseId: ''
      });
      return;
    }
    setOrderFormData({
      ...orderFormData,
      [name]: value
    });
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    
    // Validate customer information
    if (!customerFields.firstName || customerFields.firstName.trim().length === 0) {
      toast.error('Please enter customer first name');
      return;
    }
    if (customerFields.phoneNumber && !isValidPhoneNumber(customerFields.phoneNumber)) {
      toast.error('Phone number doesn\'t look valid');
      return;
    }

    const effectiveItems = getEffectiveOrderItems();
    if (!effectiveItems.length) {
      toast.error('Please add at least one product to the order');
      return;
    }

    if (orderFormData.fulfillmentMethod === 'PICKUP' && !orderFormData.retailStoreId) {
      toast.error('Please select a retail store for pick-up');
      return;
    }
    if (orderFormData.fulfillmentMethod === 'DELIVERY' && !orderFormData.warehouseId) {
      toast.error('Please select a warehouse for delivery');
      return;
    }
    
    try {
      let resolvedCustomerId = orderFormData.customerId
        ? parseInt(String(orderFormData.customerId), 10)
        : null;

      if (!resolvedCustomerId && !editingOrder) {
        const phone = (customerFields.phoneNumber || '').trim();
        if (!phone) {
          toast.error('Phone number is required. Enter a phone number to create a new customer with this order.');
          return;
        }
        const emailTrim = (customerFields.email || '').trim();
        const newCustomerPayload = {
          firstName: customerFields.firstName.trim(),
          lastName: (customerFields.lastName || '').trim() || undefined,
          phoneNumber: phone,
          customerType: 'INDIVIDUAL'
        };
        if (emailTrim) {
          newCustomerPayload.email = emailTrim;
        }
        const customerRes = await axios.post('/api/customers', newCustomerPayload);
        resolvedCustomerId = customerRes.data.id;
        setOrderFormData((prev) => ({ ...prev, customerId: resolvedCustomerId }));
        fetchCustomers(true);
      }

      if (!resolvedCustomerId) {
        toast.error('Customer not found. Match an existing customer by phone or email, or fill first name and phone to create a new one.');
        return;
      }

      // Prepare order items for submission
      const orderItemsData = effectiveItems.map(item => ({
        product: { id: item.productId },
        quantity: parseInt(item.quantity),
        unitPrice: parseFloat(item.unitPrice),
        discountPercentage: item.discountPercentage ? parseFloat(item.discountPercentage) : null,
        discountAmount: item.discountAmount ? parseFloat(item.discountAmount) : null
      }));
      
      if (editingOrder) {
        const orderData = buildOrderPayload(resolvedCustomerId, orderItemsData);
        await axios.put(`/api/orders/${editingOrder.id}`, orderData);
        toast.success('Order updated successfully');
      } else {
        // Generate order number
        const orderNumber = `ORD-${Date.now()}`;
        const orderData = {
          ...buildOrderPayload(resolvedCustomerId, orderItemsData),
          orderNumber,
          orderDate: new Date().toISOString()
        };
        const { data: createdOrder } = await axios.post('/api/orders', orderData);
        toast.success('Order created successfully');
        if (quoteSource?.kind === 'ORDER' && createdOrder?.id) await linkQuote(quoteSource, createdOrder.id);
      }
      setShowOrderModal(false);
      setEditingOrder(null);
      resetOrderForm();
      fetchOrders();
    } catch (error) {
      const data = error.response?.data;
      const message = typeof data === 'string' ? data : error.response?.data?.message;
      toast.error(message || 'Error saving order');
      console.error('Order submission error:', error);
    }
  };

  const handleEditOrder = (order) => {
    setEditingOrder(order);
    setDraftDiscountLead(null);
    setOrderFormData({
      orderNumber: order.orderNumber,
      customerId: order.customer?.id,
      orderStatus: order.orderStatus,
      paymentStatus: order.paymentStatus,
      fulfillmentMethod: order.fulfillmentMethod || 'DELIVERY',
      retailStoreId: order.retailStore?.id || '',
      warehouseId: order.warehouse?.id || '',
      notes: order.notes
    });
    // Set customer fields
    if (order.customer) {
      setCustomerFields({
        phoneNumber: order.customer.phoneNumber || '',
        email: order.customer.email || '',
        firstName: order.customer.firstName ?? '',
        lastName: order.customer.lastName ?? ''
      });
    } else {
      setCustomerFields({
        phoneNumber: '',
        email: '',
        firstName: '',
        lastName: ''
      });
    }
    // Set order items if they exist
    if (order.orderItems && order.orderItems.length > 0) {
      setOrderItems(order.orderItems.map(item => ({
        id: item.id,
        productId: item.product?.id,
        productName: item.product?.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        discountPercentage: item.discountPercentage || '',
        discountAmount: item.discountAmount || '',
        totalAmount: item.totalAmount,
        discountLead: null
      })));
    } else {
      setOrderItems([]);
    }
    setShowOrderModal(true);
  };

  const handleDeleteOrder = async (id) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await axios.delete(`/api/orders/${id}`);
        toast.success('Order deleted successfully');
        fetchOrders();
      } catch (error) {
        toast.error('Error deleting order');
      }
    }
  };

  const resetOrderForm = () => {
    setOrderFormData({
      orderNumber: '',
      customerId: '',
      orderStatus: 'PENDING',
      paymentStatus: 'PENDING',
      fulfillmentMethod: 'DELIVERY',
      retailStoreId: '',
      warehouseId: '',
      notes: ''
    });
    setOrderItems([]);
    setNewOrderItem({
      productId: '',
      quantity: 1,
      unitPrice: '',
      discountPercentage: '',
      discountAmount: ''
    });
    setDraftDiscountLead(null);
    setCustomerFields({
      phoneNumber: '',
      email: '',
      firstName: '',
      lastName: ''
    });
  };

  const handleCloseOrderModal = () => {
    setShowOrderModal(false);
    setEditingOrder(null);
    resetOrderForm();
  };

  // Customer search functionality
  // Customer field handlers with auto-population
  const handleCustomerPhoneChange = (e) => {
    const phoneNumber = e.target.value;
    setCustomerFields({ ...customerFields, phoneNumber });
    
    if (phoneNumber.trim().length > 0) {
      // Search for customer by phone number
      const foundCustomer = customers.find(customer => 
        customer.phoneNumber && customer.phoneNumber.trim().toLowerCase() === phoneNumber.trim().toLowerCase()
      );
      
      if (foundCustomer) {
        // Auto-populate other fields
        setCustomerFields({
          phoneNumber: foundCustomer.phoneNumber || '',
          email: foundCustomer.email || '',
          firstName: foundCustomer.firstName ?? '',
          lastName: foundCustomer.lastName ?? ''
        });
        setOrderFormData({ ...orderFormData, customerId: foundCustomer.id });
        toast.success('Customer found and auto-populated');
      } else {
        // Clear customer ID if no match found
        setOrderFormData({ ...orderFormData, customerId: '' });
      }
    } else {
      // Clear customer ID if phone is cleared
      setOrderFormData({ ...orderFormData, customerId: '' });
    }
  };

  const handleCustomerEmailChange = (e) => {
    const email = e.target.value;
    setCustomerFields({ ...customerFields, email });
    
    if (email.trim().length > 0) {
      // Search for customer by email
      const foundCustomer = customers.find(customer => 
        customer.email && customer.email.trim().toLowerCase() === email.trim().toLowerCase()
      );
      
      if (foundCustomer) {
        // Auto-populate other fields
        setCustomerFields({
          phoneNumber: foundCustomer.phoneNumber || '',
          email: foundCustomer.email || '',
          firstName: foundCustomer.firstName ?? '',
          lastName: foundCustomer.lastName ?? ''
        });
        setOrderFormData({ ...orderFormData, customerId: foundCustomer.id });
        toast.success('Customer found and auto-populated');
      } else {
        // Clear customer ID if no match found
        setOrderFormData({ ...orderFormData, customerId: '' });
      }
    } else {
      // Clear customer ID if email is cleared
      setOrderFormData({ ...orderFormData, customerId: '' });
    }
  };

  const handleCustomerOrderNameChange = (e) => {
    const { name, value } = e.target;
    setCustomerFields({ ...customerFields, [name]: value });
  };

  // Order items functions
  const calculateItemTotal = (item) => {
    const quantity = parseFloat(item.quantity) || 0;
    const unitPrice = parseFloat(item.unitPrice) || 0;
    const discountAmount = parseFloat(item.discountAmount) || 0;
    return (quantity * unitPrice) - discountAmount;
  };

  /**
   * Keep Discount % and Discount $ in sync for the draft line.
   * @param {'pct'|'amt'|null} prefer - user's chosen basis: keep % fixed and derive $, or keep $ fixed and derive %.
   *        null = infer (prefer % if set, else amount).
   */
  const reconcileDraftLineDiscount = (draft, prefer) => {
    const quantity = parseFloat(String(draft.quantity)) || 0;
    const unitPrice = parseFloat(String(draft.unitPrice)) || 0;
    const gross = quantity * unitPrice;

    const pctStrTrimmed =
      draft.discountPercentage !== undefined && draft.discountPercentage !== null
        ? String(draft.discountPercentage).trim()
        : '';
    const amtStrTrimmed =
      draft.discountAmount !== undefined && draft.discountAmount !== null
        ? String(draft.discountAmount).trim()
        : '';

    const pctParsed = pctStrTrimmed === '' ? NaN : parseFloat(pctStrTrimmed);
    const amtParsed = amtStrTrimmed === '' ? NaN : parseFloat(amtStrTrimmed);

    const pctProvided = pctStrTrimmed !== '' && Number.isFinite(pctParsed);
    const amtProvided = amtStrTrimmed !== '' && Number.isFinite(amtParsed);

    const next = { ...draft };

    if (prefer === 'pct' && pctProvided) {
      const pct = Math.max(0, pctParsed);
      next.discountAmount = gross > 0 ? ((gross * pct) / 100).toFixed(2) : '0.00';
      return next;
    }
    if (prefer === 'amt' && amtProvided) {
      const amt = Math.max(0, amtParsed);
      if (gross > 0) {
        next.discountPercentage = ((amt / gross) * 100).toFixed(4);
        next.discountAmount = amt.toFixed(2);
      } else {
        next.discountPercentage = '';
      }
      return next;
    }
    if (pctProvided) {
      const pct = Math.max(0, pctParsed);
      next.discountAmount = gross > 0 ? ((gross * pct) / 100).toFixed(2) : '0.00';
      return next;
    }
    if (amtProvided) {
      const amt = Math.max(0, amtParsed);
      if (gross > 0) {
        next.discountPercentage = ((amt / gross) * 100).toFixed(4);
        next.discountAmount = amt.toFixed(2);
      } else {
        next.discountPercentage = '';
      }
      return next;
    }
    return next;
  };

  /** Draft row counts as a line when product, qty ≥ 1, and unit price > 0 are set (no need to click + Add Product). */
  const isCompleteOrderItemDraft = (draft) => {
    if (!draft?.productId) return false;
    const q = parseInt(String(draft.quantity), 10);
    if (!Number.isFinite(q) || q < 1) return false;
    const price = parseFloat(String(draft.unitPrice));
    if (!Number.isFinite(price) || price <= 0) return false;
    return true;
  };

  const tryBuildOrderItemFromDraft = () => {
    if (!isCompleteOrderItemDraft(newOrderItem)) return null;
    const selectedProduct = products.find(p => p.id === parseInt(newOrderItem.productId, 10));
    if (!selectedProduct) return null;
    return {
      productId: parseInt(newOrderItem.productId, 10),
      productName: selectedProduct.name,
      quantity: parseInt(newOrderItem.quantity, 10),
      unitPrice: parseFloat(newOrderItem.unitPrice),
      discountPercentage: newOrderItem.discountPercentage ? parseFloat(newOrderItem.discountPercentage) : null,
      discountAmount: newOrderItem.discountAmount ? parseFloat(newOrderItem.discountAmount) : 0,
      totalAmount: calculateItemTotal(newOrderItem)
    };
  };

  const getEffectiveOrderItems = () => {
    const list = [...orderItems];
    const fromDraft = tryBuildOrderItemFromDraft();
    if (fromDraft) list.push(fromDraft);
    return list;
  };

  const handleAddOrderItem = () => {
    if (!newOrderItem.productId) {
      toast.error('Please select a product');
      return;
    }
    if (!newOrderItem.quantity || newOrderItem.quantity < 1) {
      toast.error('Please enter a valid quantity');
      return;
    }
    if (!newOrderItem.unitPrice || newOrderItem.unitPrice <= 0) {
      toast.error('Please enter a valid unit price');
      return;
    }

    const orderItem = tryBuildOrderItemFromDraft();
    if (!orderItem) {
      toast.error('Selected product not found');
      return;
    }

    setOrderItems([...orderItems, { ...orderItem, discountLead: draftDiscountLead }]);
    setDraftDiscountLead(null);
    setNewOrderItem({
      productId: '',
      quantity: 1,
      unitPrice: '',
      discountPercentage: '',
      discountAmount: '',
      notes: ''
    });
  };

  const handleRemoveOrderItem = (index) => {
    const updatedItems = orderItems.filter((_, i) => i !== index);
    setOrderItems(updatedItems);
  };

  const handleOrderItemChange = (index, field, value) => {
    const updatedItems = [...orderItems];
    updatedItems[index][field] = value;

    const row = updatedItems[index];
    const quantity = parseFloat(row.quantity) || 0;
    const unitPrice = parseFloat(row.unitPrice) || 0;
    const gross = quantity * unitPrice;

    const pctStr =
      row.discountPercentage !== undefined && row.discountPercentage !== null
        ? String(row.discountPercentage).trim()
        : '';
    const pct = pctStr === '' ? NaN : parseFloat(pctStr);
    const pctProvided = pctStr !== '' && Number.isFinite(pct);

    const amtNum = parseFloat(row.discountAmount);
    const amtProvided = Number.isFinite(amtNum);

    const lead = row.discountLead ?? null;

    if (field === 'discountPercentage' && pctProvided) {
      updatedItems[index].discountLead = 'pct';
      updatedItems[index].discountAmount =
        gross > 0 && pct > 0 ? (gross * pct) / 100 : 0;
    } else if (field === 'discountAmount' && amtProvided && gross > 0) {
      updatedItems[index].discountLead = 'amt';
      updatedItems[index].discountPercentage = (amtNum / gross) * 100;
    } else if (field === 'quantity' || field === 'unitPrice') {
      if (lead === 'pct' && pctProvided) {
        updatedItems[index].discountAmount =
          pct <= 0 ? 0 : (quantity * unitPrice * pct) / 100;
      } else if (lead === 'amt' && amtProvided && gross > 0) {
        updatedItems[index].discountPercentage = (amtNum / gross) * 100;
      } else if (pctProvided) {
        updatedItems[index].discountAmount =
          pct <= 0 ? 0 : (quantity * unitPrice * pct) / 100;
      } else if (amtProvided && gross > 0) {
        updatedItems[index].discountPercentage = (amtNum / gross) * 100;
      }
    }

    if (
      field === 'quantity' ||
      field === 'unitPrice' ||
      field === 'discountAmount' ||
      field === 'discountPercentage'
    ) {
      updatedItems[index].totalAmount = calculateItemTotal(updatedItems[index]);
    }

    setOrderItems(updatedItems);
  };

  const defaultUnitPriceFromProduct = (product) => {
    if (!product || product.price == null || product.price === '') {
      return '';
    }
    return String(product.price);
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === parseInt(productId, 10));
    if (product) {
      setNewOrderItem(
        reconcileDraftLineDiscount(
          {
            ...newOrderItem,
            productId: productId,
            unitPrice: orderRates[productId]?.rate != null ? String(Number(orderRates[productId].rate)) : defaultUnitPriceFromProduct(product)
          },
          draftDiscountLead
        )
      );
    }
  };

  const calculateOrderTotal = () => {
    let sum = orderItems.reduce((acc, item) => acc + (item.totalAmount || 0), 0);
    if (isCompleteOrderItemDraft(newOrderItem)) {
      sum += calculateItemTotal(newOrderItem);
    }
    return sum;
  };

  const hasAtLeastOneOrderLine = () => getEffectiveOrderItems().length > 0;

  const getStatusBadge = (status) => {
    const statusMap = {
      'PENDING': 'warning',
      'CONFIRMED': 'info',
      'PROCESSING': 'primary',
      'SHIPPED': 'success',
      'DELIVERED': 'success',
      'CANCELLED': 'danger',
      'PAID': 'success',
      'PARTIAL': 'warning',
      'REFUNDED': 'danger'
    };
    return statusMap[status] || 'secondary';
  };

  if (loading && activeTab === 'customers') {
    return (
      <Container className="mt-4">
        <div className="d-flex justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      {/* Alerts for Sales (which ones, and for whom, is decided by the server; see alerts/alertRegistry.js) */}
      <ModuleAlerts module="sales" section={activeTab} />
      <Row>
        <Col>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
            {/* Customers Tab */}
            <Tab 
              eventKey="customers" 
              title="Customers"
            >
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">Manage customer information and relationships</p>
                  <Button
                    variant="primary"
                    onClick={async () => {
                      setEditingCustomer(null);
                      await resetCustomerForm();
                      setShowCustomerModal(true);
                    }}
                  >
                    + Add Customer
                  </Button>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Card>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Company</th>
                            <th>GSTIN</th>
                            <th>Type</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {customers.map((customer) => (
                            <tr key={customer.id}>
                              <td>{formatCustomerDisplayName(customer)}</td>
                              <td>{customer.email}</td>
                              <td>{customer.phoneNumber || '-'}</td>
                              <td>{customer.companyName || '-'}</td>
                              <td>
                                {customer.taxId || '-'}
                                {customer.gstRegistrationType === 'COMPOSITION' && <Badge bg="secondary" className="ms-1">Composition</Badge>}
                              </td>
                              <td>{customer.customerType}</td>
                              <td>
                                {storeEnabled && (
                                  <Button size="sm" variant="outline-success" className="me-2" onClick={() => setStoreLinkCustomer(customer)}
                                    title="A private link to your online store with this customer's own prices" data-testid="store-link">
                                    Store link
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => handleViewPrint(customer, 'CUSTOMER')}
                                  title="View & Print"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEditCustomer(customer)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteCustomer(customer.id)}
                                >
                                  Delete
                                </Button>
                                <HistoryButton documentType="Customer" documentId={customer.id} label={formatCustomerDisplayName(customer)} className="ms-1" />
                              </td>
                            </tr>
                          ))}
                          {customers.length === 0 && (
                            <tr>
                              <td colSpan="6" className="text-center text-muted">
                                No customers found. Create one to get started.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab>

            {/* Product Catalog Tab */}
            <Tab
              eventKey="catalog"
              title="Product Catalog"
            >
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">
                    Sellable products and list prices ({organizationCurrency}). Product maintenance and photos are in Org Admin → Products.
                  </p>
                  {canShareCatalog && (
                    <Button variant="success" size="sm" className="text-nowrap ms-3" onClick={() => setShareCatalog(true)} data-testid="share-catalog">
                      <i className="bi bi-share me-1" />Share catalog
                    </Button>
                  )}
                </div>

                {catalogLoading ? (
                  <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  </div>
                ) : (
                  <Card>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th style={{ width: 56 }} />
                            <th>Product</th>
                            <th>SKU</th>
                            <th>Category</th>
                            <th>Brand</th>
                            <th>UOM</th>
                            <th>List price</th>
                            <th>Stock Qty</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...toArray(products)]
                            .sort((a, b) =>
                              String(a.name || '').localeCompare(String(b.name || ''), undefined, {
                                sensitivity: 'base'
                              })
                            )
                            .map((product) => (
                              <tr key={product.id}>
                                <td className="text-center"><ProductThumb productId={product.id} imageId={mainPhotos[product.id]} size={40} url={mainPhotoUrls[product.id] || null} /></td>
                                <td>
                                  <span title={product.description || ''}>{product.name}</span>
                                </td>
                                <td>{product.sku || '—'}</td>
                                <td>{product.category || '—'}</td>
                                <td>{product.brand || '—'}</td>
                                <td>{product.unitOfMeasure || '—'}</td>
                                <td>
                                  {product.price != null && product.price !== ''
                                    ? formatCurrency(product.price)
                                    : '—'}
                                </td>
                                <td>{product.stockQuantity ?? '—'}</td>
                                <td>
                                  <Badge bg={product.isActive !== false ? 'success' : 'secondary'}>
                                    {product.isActive !== false ? 'Active' : 'Inactive'}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          {toArray(products).length === 0 && (
                            <tr>
                              <td colSpan="9" className="text-center text-muted">
                                No products in catalog. Add products under Org Admin → Products.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab>

            {/* Price lists: only where switched on, for the people who keep them */}
            {canManagePriceLists && (
              <Tab eventKey="price-lists" title="Price Lists">
                <PriceLists active={activeTab === 'price-lists'} />
              </Tab>
            )}

            {/* Quotations Tab */}
            <Tab
              eventKey="quotations"
              title="Quotations"
            >
              <Quotations active={activeTab === 'quotations'} customers={customers} products={products} taxRates={taxRates}
                orgState={orgAddressDefaults?.state} currency={organizationCurrency} reloadKey={quotesReloadKey}
                onMakeBill={makeBillFromQuote} onMakeOrder={makeOrderFromQuote} />
            </Tab>

            {/* Sale Order Tab */}
            <Tab
              eventKey="orders"
              title="Sale Order"
            >
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">Manage sales orders and track order status</p>
                  <Button variant="primary" onClick={() => setShowOrderModal(true)}>
                    + Add Order
                  </Button>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Card>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Order Number</th>
                            <th>Customer</th>
                            <th>Date</th>
                            <th>Items</th>
                            <th>Fulfillment Method</th>
                            <th>Fulfill from</th>
                            <th>Status</th>
                            <th>Payment Status</th>
                            <th>Total</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {toArray(orders).map((order) => (
                            <tr key={order.id}>
                              <td>{order.orderNumber}{order.source === 'WEB' && <Badge bg="info" className="ms-2" title="Placed by a buyer on your online store">Online</Badge>}</td>
                              <td>{formatCustomerDisplayName(order.customer)}</td>
                              <td>{order.orderDate ? formatDate(order.orderDate) : '-'}</td>
                              <td>
                                {order.orderItems && order.orderItems.length > 0 ? (
                                  <span title={order.orderItems.map(item => `${item.product?.name || 'N/A'} (${item.quantity})`).join(', ')}>
                                    {order.orderItems.length} item{order.orderItems.length !== 1 ? 's' : ''}
                                  </span>
                                ) : (
                                  <span className="text-muted">0 items</span>
                                )}
                              </td>
                              <td>{formatFulfillmentMethod(order.fulfillmentMethod)}</td>
                              <td>{formatFulfillmentLocation(order)}</td>
                              <td>
                                <Badge bg={getStatusBadge(order.orderStatus)}>
                                  {order.orderStatus}
                                </Badge>
                              </td>
                              <td>
                                <Badge bg={getStatusBadge(order.paymentStatus)}>
                                  {order.paymentStatus}
                                </Badge>
                              </td>
                              <td>{formatCurrency(order.totalAmount)}</td>
                              <td>
                                <Button
                                  size="sm"
                                  variant="outline-info"
                                  className="me-2"
                                  onClick={() => handleViewPrint(order, 'ORDER')}
                                  title="View & Print"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ verticalAlign: 'middle' }}>
                                    <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                    <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                  </svg>
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  className="me-2"
                                  onClick={() => handleEditOrder(order)}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteOrder(order.id)}
                                >
                                  Delete
                                </Button>
                                <HistoryButton documentType="Order" documentId={order.id} label={`Sale order ${order.orderNumber || order.id}`} className="ms-1" />
                              </td>
                            </tr>
                          ))}
                          {toArray(orders).length === 0 && (
                            <tr>
                              <td colSpan="10" className="text-center text-muted">
                                No orders found. Create one to get started.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab>

            {/* Sale Bill Tab */}
            <Tab
              eventKey="salebills"
              title="Sale Bill"
            >
              <div className="mt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted mb-0">Tax invoices — issuing a bill reduces physical stock and posts AR/Revenue/Tax ledger entries</p>
                  <div className="d-flex gap-2">
                    <Button variant="outline-secondary" onClick={handleExportSalesRegister}>
                      Export CSV
                    </Button>
                    <Button variant="primary" onClick={handleCreateSaleBill}>
                      + Create Sale Bill
                    </Button>
                  </div>
                </div>

                {loading ? (
                  <div className="d-flex justify-content-center">
                    <Spinner animation="border" role="status" />
                  </div>
                ) : (
                  <Card>
                    <Card.Body>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>Bill Number</th>
                            <th>Customer</th>
                            <th>Date</th>
                            <th>Sale Order</th>
                            <th>Fulfill from</th>
                            <th>Status</th>
                            <th>Payment Status</th>
                            <th>e-Invoice</th>
                            <th>Taxable</th>
                            <th>Tax</th>
                            <th>Total</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {toArray(saleBills).map((bill) => (
                            <tr key={bill.id}>
                              <td>
                                <strong>{bill.billNumber}</strong>
                                {bill.documentKind === 'OPENING' && (
                                  <Badge bg="secondary" className="ms-1" title="Brought forward from before your opening balance date; not a sale">Opening due</Badge>
                                )}
                              </td>
                              <td>{formatCustomerDisplayName(bill.customer) || '-'}</td>
                              <td>{formatDate(bill.billDate) || '-'}</td>
                              <td>{bill.salesOrder?.orderNumber || '-'}</td>
                              <td>{bill.warehouse?.name || bill.retailStore?.name || bill.sourceProductionUnit?.name || '-'}</td>
                              <td><Badge bg={getStatusBadge(bill.status)}>{bill.status}</Badge>
                                {bill.creditOverrideBy && (
                                  <Badge bg="warning" text="dark" className="ms-1" title={`Issued over the credit limit (${formatCurrency(bill.creditExposureAtIssue)} against ${formatCurrency(bill.creditLimitAtIssue)}) by ${bill.creditOverrideBy}: ${bill.creditOverrideReason}`}>
                                    Over limit
                                  </Badge>
                                )}
                              </td>
                              <td><Badge bg={getStatusBadge(bill.paymentStatus)}>{bill.paymentStatus}</Badge></td>
                              <td><GstRegistrationBadges summary={gstSummaries[bill.id]} /></td>
                              <td>{formatCurrency(bill.taxableAmount)}</td>
                              <td>{formatCurrency(bill.totalTaxAmount)}</td>
                              <td>{formatCurrency(bill.totalAmount)}</td>
                              <td>
                                {bill.documentKind !== 'OPENING' && (
                                  <Button
                                    size="sm"
                                    variant="outline-info"
                                    className="me-2"
                                    onClick={() => handleViewPrint(bill, 'SALE_BILL')}
                                    title="View & Print"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                      <path d="M5 1a2 2 0 0 0-2 2v1h10V3a2 2 0 0 0-2-2H5zm6 8H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1z"/>
                                      <path d="M0 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-2a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v2H2a2 2 0 0 1-2-2V7zm2.5 1a.5.5 0 1 0 0-1 .5.5 0 0 0 0 1z"/>
                                    </svg>
                                  </Button>
                                )}
                                {bill.status === 'ISSUED' && bill.documentKind !== 'OPENING' && (
                                  <>
                                    {canShareBills && (
                                      <Button
                                        size="sm"
                                        variant="outline-success"
                                        className="me-2"
                                        title="Send the bill to the customer on WhatsApp"
                                        onClick={() => setShareBill(bill)}
                                      >
                                        <i className="bi bi-whatsapp" />
                                      </Button>
                                    )}
                                    {(gstEnabled.einvoiceEnabled || gstEnabled.ewayEnabled || gstSummaries[bill.id]) && (
                                      <Button
                                        size="sm"
                                        variant="outline-success"
                                        className="me-2"
                                        onClick={() => setGstDialog(bill)}
                                      >
                                        e-Invoice / e-Way
                                      </Button>
                                    )}
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      className="me-2"
                                      onClick={() => setNoteDialog({ bill, type: 'CREDIT' })}
                                    >
                                      Credit note
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-secondary"
                                      className="me-2"
                                      onClick={() => setNoteDialog({ bill, type: 'DEBIT' })}
                                    >
                                      Debit note
                                    </Button>
                                  </>
                                )}
                                {bill.status === 'DRAFT' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline-primary"
                                      className="me-2"
                                      onClick={() => handleEditSaleBill(bill)}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() => handleDeleteSaleBill(bill.id)}
                                    >
                                      Delete
                                    </Button>
                                  </>
                                )}
                                <HistoryButton documentType="SaleBill" documentId={bill.id} label={`Sale bill ${bill.billNumber || bill.id}`} className="ms-1" />
                              </td>
                            </tr>
                          ))}
                          {toArray(saleBills).length === 0 && (
                            <tr>
                              <td colSpan="12" className="text-center text-muted">
                                No Sale Bills found. Create one to get started.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                )}
              </div>
            </Tab>

            {/* Credit / Debit Notes Tab */}
            <Tab
              eventKey="notes"
              title="Credit / Debit Notes"
            >
              <SaleNotes active={activeTab === 'notes'} refreshKey={notesRefreshKey} />
            </Tab>

            {/* Sales and gross profit reports: managers only, as they show margins */}
            {canSeeReports && (
              <Tab
                eventKey="reports"
                title="Reports"
              >
                <SalesReports active={activeTab === 'reports'} currency={organizationCurrency} />
              </Tab>
            )}
          </Tabs>
        </Col>
      </Row>

      <Modal show={Boolean(creditWarning)} onHide={() => setCreditWarning(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Over the credit limit</Modal.Title>
        </Modal.Header>
        {creditWarning && (
          <>
            <Modal.Body>
              <Alert variant="warning" className="mb-3">
                Issuing this bill takes <strong>{creditWarning.customerName}</strong> {formatCurrency(creditWarning.excess)} over their credit limit.
              </Alert>
              <Table size="sm" borderless className="mb-3">
                <tbody>
                  <tr><td>Credit limit</td><td className="text-end">{formatCurrency(creditWarning.creditLimit)}</td></tr>
                  <tr><td>Owed now (net of advances)</td><td className="text-end">{formatCurrency(creditWarning.currentBalance)}</td></tr>
                  <tr><td>This bill</td><td className="text-end">{formatCurrency(creditWarning.billAmount)}</td></tr>
                  <tr className="fw-bold border-top"><td>Would owe</td><td className="text-end">{formatCurrency(creditWarning.projected)}</td></tr>
                </tbody>
              </Table>
              {creditWarning.canOverride ? (
                <Form.Group>
                  <Form.Label>Reason for issuing anyway</Form.Label>
                  <Form.Control
                    as="textarea" rows={2} maxLength={500} value={creditWarning.reason}
                    onChange={(e) => setCreditWarning({ ...creditWarning, reason: e.target.value })}
                  />
                  <Form.Text className="text-muted">Kept on the bill with your name and these figures.</Form.Text>
                </Form.Group>
              ) : (
                <p className="mb-0 text-muted">Only a manager can issue a bill over a credit limit. Ask one to issue it, or collect a payment first.</p>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setCreditWarning(null)}>Cancel</Button>
              {creditWarning.canOverride && (
                <Button
                  variant="warning" disabled={!creditWarning.reason.trim()}
                  onClick={() => handleSaveSaleBill(creditWarning.status, creditWarning.reason.trim())}
                >
                  Issue anyway
                </Button>
              )}
            </Modal.Footer>
          </>
        )}
      </Modal>

      <GstRegistrationModal
        show={Boolean(gstDialog)}
        docType="SALE_BILL"
        docId={gstDialog?.id}
        label={gstDialog?.billNumber}
        onHide={() => setGstDialog(null)}
        canCancel={hasAnyRole(user, ['ORG_ADMIN', 'SALES_MGR'])}
        onChanged={reloadGstSummaries}
      />

      {shareCatalog && (
        <CatalogShareModal
          categories={[...new Set(toArray(products).filter((p) => p.isActive !== false && p.category).map((p) => p.category))].sort((a, b) => a.localeCompare(b))}
          onHide={() => setShareCatalog(false)}
        />
      )}
      {storeLinkCustomer && (
        <WhatsAppShareModal path={`trade-store/${storeLinkCustomer.id}`} title={`Store link for ${storeLinkCustomer.companyName || storeLinkCustomer.firstName || 'this customer'}`}
          what="store" offerUpi={false} days={365} stopPath={(l) => `/api/share/trade-store/links/${l.id}/stop`} onHide={() => setStoreLinkCustomer(null)} />
      )}
      {shareBill && (
        <WhatsAppShareModal path={`sale-bills/${shareBill.id}`} title={`Send bill ${shareBill.billNumber}`}
          what="invoice" onHide={() => setShareBill(null)} />
      )}
      <SaleNoteModal
        show={Boolean(noteDialog)}
        bill={noteDialog?.bill}
        noteType={noteDialog?.type}
        currency={organizationCurrency}
        onHide={() => setNoteDialog(null)}
        onIssued={() => {
          setNotesRefreshKey((k) => k + 1);
          fetchSaleBills();
        }}
      />

      {/* Print View Modal */}
      <Modal show={showPrintModal} onHide={() => setShowPrintModal(false)} size="xl" className="print-modal">
        <Modal.Header className="print-header">
          <Modal.Title>
            {printType === 'CUSTOMER' && 'Customer'}
            {printType === 'ORDER' && 'Order'}
            {printType === 'SALE_BILL' && 'Sale Bill'}
          </Modal.Title>
          <div className="print-actions">
            <Button variant="primary" onClick={handlePrint} className="me-2">Print</Button>
            <Button variant="secondary" onClick={() => setShowPrintModal(false)}>Close</Button>
          </div>
        </Modal.Header>
        <Modal.Body className="print-body">
          {printType === 'SALE_BILL' && printData && (
            <>
              <TaxInvoice billId={printData.id} currency={organizationCurrency} />
              {printData.status === 'ISSUED' && <GstRegistrationPrintBlock docType="SALE_BILL" docId={printData.id} />}
            </>
          )}
          {printType !== 'SALE_BILL' && (
          <OrgPrintChrome>
          {printType === 'CUSTOMER' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>CUSTOMER</h2>
                <p className="text-muted"><strong>{formatCustomerDisplayName(printData)}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>First name:</strong> {printData.firstName || '-'}</p>
                  <p><strong>Last name:</strong> {printData.lastName || '-'}</p>
                  <p><strong>Email:</strong> {printData.email || '-'}</p>
                  <p><strong>Phone:</strong> {printData.phoneNumber || '-'}</p>
                  <p><strong>Company:</strong> {printData.companyName || '-'}</p>
                  <p><strong>Type:</strong> {printData.customerType || '-'}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Address:</strong> {printData.address || '-'}</p>
                  <p><strong>City:</strong> {printData.city || '-'}</p>
                  <p><strong>District:</strong> {printData.district || '-'}</p>
                  <p><strong>State:</strong> {printData.state || '-'}</p>
                  <p><strong>Country:</strong> {printData.country || '-'}</p>
                  <p><strong>Postal Code:</strong> {printData.postalCode || '-'}</p>
                  <p><strong>Tax ID / GSTIN:</strong> {printData.taxId || '-'}</p>
                </Col>
              </Row>
            </div>
          )}
          {printType === 'ORDER' && printData && (
            <div className="print-content">
              <div className="text-center mb-4">
                <h2>ORDER</h2>
                <p className="text-muted"><strong>{printData.orderNumber}</strong></p>
              </div>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Customer:</strong> {formatCustomerDisplayName(printData.customer) || '-'}</p>
                  <p><strong>Date:</strong> {printData.orderDate ? formatDate(printData.orderDate) : '-'}</p>
                  <p><strong>Status:</strong> {printData.orderStatus || '-'}</p>
                  <p><strong>Payment Status:</strong> {printData.paymentStatus || '-'}</p>
                  <p><strong>Fulfillment Method:</strong> {formatFulfillmentMethod(printData.fulfillmentMethod)}</p>
                  <p><strong>Fulfill from:</strong> {formatFulfillmentLocation(printData)}</p>
                  <p><strong>Total:</strong> {formatCurrency(printData.totalAmount)}</p>
                </Col>
                <Col md={6}>
                  {printData.notes && <p><strong>Notes:</strong> {printData.notes}</p>}
                </Col>
              </Row>
              {printData.orderItems && printData.orderItems.length > 0 && (
                <>
                  <h5 className="mt-3 mb-2">Order Items</h5>
                  <Table striped bordered size="sm">
                    <thead><tr><th>Product</th><th>Qty</th><th>Unit Price</th><th>Discount</th><th>Line Total</th></tr></thead>
                    <tbody>
                      {printData.orderItems.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.product?.name || '-'}</td>
                          <td>{item.quantity || '-'}</td>
                          <td>{item.unitPrice != null ? formatCurrency(item.unitPrice) : '-'}</td>
                          <td>
                            {item.discountPercentage != null
                              ? `${item.discountPercentage}%`
                              : item.discountAmount != null
                                ? formatCurrency(item.discountAmount)
                                : '-'}
                          </td>
                          <td>
                            {item.lineTotal != null
                              ? formatCurrency(item.lineTotal)
                              : item.totalAmount != null
                                ? formatCurrency(item.totalAmount)
                                : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </>
              )}
            </div>
          )}
                  </OrgPrintChrome>
          )}
        </Modal.Body>
      </Modal>

      {/* Customer Modal */}
      <Modal show={showCustomerModal} onHide={handleCloseCustomerModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCustomer ? 'Edit Customer' : 'Add Customer'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCustomerSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Country</Form.Label>
                  <Form.Select
                    value={selectedCustomerCountryCode}
                    onChange={(e) => {
                      const countryCode = e.target.value;
                      const countryObj = countries.find((c) => c.code === countryCode);
                      const countryName = countryObj ? countryObj.name : '';
                      setSelectedCustomerCountryCode(countryCode);
                      setCustomerFormData((prev) => ({
                        ...prev,
                        country: countryName,
                        state: ''
                      }));
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{selectedCustomerCountryCode === 'IN' ? 'GSTIN' : 'Tax ID'}</Form.Label>
                  {selectedCustomerCountryCode === 'IN' ? (
                    <GstinLookupInput
                      value={customerFormData.taxId}
                      onChange={handleCustomerInputChange}
                      onResolved={handleGstinResolved}
                    />
                  ) : (
                    <Form.Control
                      type="text"
                      name="taxId"
                      value={customerFormData.taxId}
                      onChange={handleCustomerInputChange}
                      placeholder="Tax ID"
                    />
                  )}
                </Form.Group>
                {selectedCustomerCountryCode === 'IN' && (
                  <Form.Group className="mb-3" controlId="customer-gst-registration">
                    <Form.Label>GST registration</Form.Label>
                    <Form.Select
                      name="gstRegistrationType"
                      value={customerFormData.gstRegistrationType}
                      onChange={handleCustomerInputChange}
                    >
                      <option value="">Not chosen (Regular with a GSTIN, otherwise Unregistered)</option>
                      <option value="REGULAR">Regular</option>
                      <option value="COMPOSITION">Composition</option>
                      <option value="UNREGISTERED">Not registered (consumer)</option>
                    </Form.Select>
                    {customerFormData.gstRegistrationType && customerFormData.gstRegistrationType !== 'UNREGISTERED' && !(customerFormData.taxId || '').trim() && (
                      <Form.Text className="text-warning d-block">
                        Add the GSTIN so bills to this customer are reported as business sales (B2B).
                      </Form.Text>
                    )}
                  </Form.Group>
                )}
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="firstName"
                    value={customerFormData.firstName}
                    onChange={handleCustomerInputChange}
                    required
                    autoComplete="given-name"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="lastName"
                    value={customerFormData.lastName}
                    onChange={handleCustomerInputChange}
                    autoComplete="family-name"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number *</Form.Label>
                  <PhoneNumberInput
                    name="phoneNumber"
                    value={customerFormData.phoneNumber}
                    onChange={handleCustomerInputChange}
                    defaultCountry={selectedCustomerCountryCode || undefined}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={customerFormData.email}
                    onChange={handleCustomerInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Type</Form.Label>
                  <Form.Select
                    name="customerType"
                    value={customerFormData.customerType}
                    onChange={handleCustomerInputChange}
                  >
                    <option value="INDIVIDUAL">Individual</option>
                    <option value="BUSINESS">Business</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Company Name</Form.Label>
              <Form.Control
                type="text"
                name="companyName"
                value={customerFormData.companyName}
                onChange={handleCustomerInputChange}
              />
            </Form.Group>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    name="address"
                    value={customerFormData.address}
                    onChange={handleCustomerInputChange}
                    placeholder="Street address"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={customerFormData.city}
                    onChange={handleCustomerInputChange}
                    placeholder="City"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>District</Form.Label>
                  <Form.Control
                    type="text"
                    name="district"
                    value={customerFormData.district}
                    onChange={handleCustomerInputChange}
                    placeholder="District"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>State/Province</Form.Label>
                  {selectedCustomerCountryCode && selectedCustomerCountryCode !== 'OTHER' && getStatesForCountry(selectedCustomerCountryCode).length > 0 ? (
                    <Form.Select
                      name="state"
                      value={customerFormData.state}
                      onChange={handleCustomerInputChange}
                    >
                      <option value="">Select State/Province</option>
                      {getStatesForCountry(selectedCustomerCountryCode).map((st) => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      name="state"
                      value={customerFormData.state}
                      onChange={handleCustomerInputChange}
                      placeholder="State/Province"
                    />
                  )}
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Postal Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="postalCode"
                    value={customerFormData.postalCode}
                    onChange={handleCustomerInputChange}
                    placeholder="Postal Code"
                  />
                </Form.Group>
              </Col>
            </Row>

            <h6 className="mt-2 mb-2">Credit terms</h6>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Credit Days</Form.Label>
                  <Form.Control
                    type="number"
                    name="creditDays"
                    min="0"
                    max="365"
                    step="1"
                    value={customerFormData.creditDays}
                    onChange={handleCustomerInputChange}
                    placeholder="0"
                  />
                  <Form.Text muted>Bills fall due this many days after the bill date</Form.Text>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Credit Limit</Form.Label>
                  <Form.Control
                    type="number"
                    name="creditLimit"
                    min="0"
                    step="0.01"
                    value={customerFormData.creditLimit}
                    onChange={handleCustomerInputChange}
                    placeholder="No limit"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Terms</Form.Label>
                  <Form.Control
                    type="text"
                    name="paymentTerms"
                    maxLength={50}
                    value={customerFormData.paymentTerms}
                    onChange={handleCustomerInputChange}
                    placeholder="e.g. 50% advance, balance on delivery"
                  />
                </Form.Group>
              </Col>
              {priceListsEnabled && priceLists.length > 0 && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Price list</Form.Label>
                    <Form.Select name="priceListId" value={customerFormData.priceListId} onChange={handleCustomerInputChange} data-testid="customer-price-list">
                      <option value="">Standard prices</option>
                      {priceLists.map((l) => <option key={l.id} value={l.id}>{l.name}{l.active ? '' : ' (not in use)'}</option>)}
                    </Form.Select>
                    <Form.Text muted>Quotations, orders and bills for this customer start from this list&apos;s prices</Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCustomerModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              {editingCustomer ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Order Modal */}
      <Modal show={showOrderModal} onHide={handleCloseOrderModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingOrder ? 'Edit Order' : 'Add Order'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleOrderSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Order Number</Form.Label>
                  <Form.Control
                    type="text"
                    name="orderNumber"
                    value={orderFormData.orderNumber}
                    onChange={handleOrderInputChange}
                    disabled={!editingOrder}
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <div className="mb-3">
                  <Form.Label style={{ color: 'var(--bs-body-color)' }}>Customer Information *</Form.Label>
                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'var(--bs-body-color)' }}>Phone Number</Form.Label>
                        <PhoneNumberInput
                          value={customerFields.phoneNumber}
                          onChange={handleCustomerPhoneChange}
                        />
                        <Form.Text className="text-muted">
                          Enter phone to auto-populate customer details
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'var(--bs-body-color)' }}>Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Enter email address"
                          value={customerFields.email}
                          onChange={handleCustomerEmailChange}
                          autoComplete="off"
                        />
                        <Form.Text className="text-muted">
                          Enter email to auto-populate customer details
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'var(--bs-body-color)' }}>First Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="firstName"
                          placeholder="First name"
                          value={customerFields.firstName}
                          onChange={handleCustomerOrderNameChange}
                          required={!orderFormData.customerId}
                          autoComplete="given-name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'var(--bs-body-color)' }}>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="lastName"
                          placeholder="Last name"
                          value={customerFields.lastName}
                          onChange={handleCustomerOrderNameChange}
                          autoComplete="family-name"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  {orderFormData.customerId && (
                    <input
                      type="hidden"
                      name="customerId"
                      value={orderFormData.customerId}
                    />
                  )}
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Order Status</Form.Label>
                  <Form.Select
                    name="orderStatus"
                    value={orderFormData.orderStatus}
                    onChange={handleOrderInputChange}
                  >
                    <option value="PENDING">Pending</option>
                    <option value="CONFIRMED">Confirmed</option>
                    <option value="PROCESSING">Processing</option>
                    <option value="SHIPPED">Shipped</option>
                    <option value="DELIVERED">Delivered</option>
                    <option value="CANCELLED">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Status</Form.Label>
                  <Form.Select
                    name="paymentStatus"
                    value={orderFormData.paymentStatus}
                    onChange={handleOrderInputChange}
                  >
                    <option value="PENDING">Pending</option>
                    <option value="PAID">Paid</option>
                    <option value="PARTIAL">Partial</option>
                    <option value="REFUNDED">Refunded</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Fulfillment Method</Form.Label>
                  <Form.Select
                    name="fulfillmentMethod"
                    value={orderFormData.fulfillmentMethod}
                    onChange={handleOrderInputChange}
                  >
                    <option value="DELIVERY">Delivery</option>
                    <option value="PICKUP">Pick-up</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                {orderFormData.fulfillmentMethod === 'PICKUP' ? (
                  <Form.Group className="mb-3">
                    <Form.Label>Retail store *</Form.Label>
                    <Form.Select
                      name="retailStoreId"
                      value={orderFormData.retailStoreId}
                      onChange={handleOrderInputChange}
                      required
                    >
                      <option value="">Select retail store</option>
                      {retailStores.map((store) => (
                        <option key={store.id} value={store.id}>
                          {store.name}
                          {store.city ? ` — ${store.city}` : ''}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Where the customer will pick up the order
                    </Form.Text>
                  </Form.Group>
                ) : (
                  <Form.Group className="mb-3">
                    <Form.Label>Warehouse *</Form.Label>
                    <Form.Select
                      name="warehouseId"
                      value={orderFormData.warehouseId}
                      onChange={handleOrderInputChange}
                      required
                    >
                      <option value="">Select warehouse</option>
                      {warehouses.map((warehouse) => (
                        <option key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                          {warehouse.city ? ` — ${warehouse.city}` : ''}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Warehouse that will fulfill delivery
                    </Form.Text>
                  </Form.Group>
                )}
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={orderFormData.notes}
                onChange={handleOrderInputChange}
              />
            </Form.Group>

            {/* Order Items Section */}
            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5>Order Items</h5>
                <Button variant="outline-primary" size="sm" onClick={handleAddOrderItem}>
                  + Add Product
                </Button>
              </div>

              {/* Add New Order Item Form */}
              <Card className="mb-3" style={{ backgroundColor: 'var(--bs-secondary-bg)' }}>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'var(--bs-body-color)' }}>Product *</Form.Label>
                        <Form.Select
                          value={newOrderItem.productId}
                          onChange={(e) => handleProductSelect(e.target.value)}
                        >
                          <option value="">Select Product</option>
                          {products.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name} {product.sku ? `(${product.sku})` : ''}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'var(--bs-body-color)' }}>Quantity *</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={newOrderItem.quantity}
                          onChange={(e) =>
                            setNewOrderItem(
                              reconcileDraftLineDiscount(
                                { ...newOrderItem, quantity: e.target.value },
                                draftDiscountLead
                              )
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'var(--bs-body-color)' }}>Unit Price *</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          value={newOrderItem.unitPrice}
                          onChange={(e) =>
                            setNewOrderItem(
                              reconcileDraftLineDiscount(
                                { ...newOrderItem, unitPrice: e.target.value },
                                draftDiscountLead
                              )
                            )
                          }
                        />
                        <LastRateHint last={orderRates[newOrderItem.productId]} current={newOrderItem.unitPrice}
                          onUse={() => setNewOrderItem(reconcileDraftLineDiscount(
                            { ...newOrderItem, unitPrice: String(Number(orderRates[newOrderItem.productId].rate)) }, draftDiscountLead))} />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'var(--bs-body-color)' }}>Discount %</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          value={newOrderItem.discountPercentage}
                          onChange={(e) => {
                            const v = e.target.value;
                            const merged = { ...newOrderItem, discountPercentage: v };
                            const pctEmpty = String(v).trim() === '';
                            const amtEmpty = String(merged.discountAmount ?? '').trim() === '';
                            const nextLead = pctEmpty && amtEmpty ? null : 'pct';
                            setDraftDiscountLead(nextLead);
                            setNewOrderItem(reconcileDraftLineDiscount(merged, nextLead ? 'pct' : null));
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group className="mb-3">
                        <Form.Label style={{ color: 'var(--bs-body-color)' }}>Discount Amount</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          value={newOrderItem.discountAmount}
                          onChange={(e) => {
                            const v = e.target.value;
                            const merged = { ...newOrderItem, discountAmount: v };
                            const pctEmpty = String(merged.discountPercentage ?? '').trim() === '';
                            const amtEmpty = String(v).trim() === '';
                            const nextLead = pctEmpty && amtEmpty ? null : 'amt';
                            setDraftDiscountLead(nextLead);
                            setNewOrderItem(reconcileDraftLineDiscount(merged, nextLead ? 'amt' : null));
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="align-items-center mt-1">
                    <Col className="text-end">
                      <span className="text-muted me-2">Line total:</span>
                      <strong>
                        {isCompleteOrderItemDraft(newOrderItem)
                          ? formatCurrency(calculateItemTotal(newOrderItem))
                          : '—'}
                      </strong>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* Order Items List (lines added via + Add Product) */}
              {orderItems.length > 0 && (
                <Table striped bordered hover responsive className="mb-3">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Discount</th>
                      <th>Line total</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderItems.map((item, index) => (
                      <tr key={index}>
                        <td>{item.productName}</td>
                        <td>
                          <Form.Control
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleOrderItemChange(index, 'quantity', parseInt(e.target.value, 10))}
                            style={{ width: '80px' }}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unitPrice}
                            onChange={(e) => handleOrderItemChange(index, 'unitPrice', parseFloat(e.target.value))}
                            style={{ width: '100px' }}
                          />
                        </td>
                        <td>{formatCurrency(item.discountAmount)}</td>
                        <td>{formatCurrency(item.totalAmount)}</td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveOrderItem(index)}
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              {orderItems.length === 0 && !isCompleteOrderItemDraft(newOrderItem) && (
                <div className="text-center p-3 mb-3" style={{ 
                  backgroundColor: 'var(--bs-secondary-bg)', 
                  color: 'var(--bs-body-color)',
                  borderRadius: '0.375rem' 
                }}>
                  No products added. Select a product, quantity, and unit price above, or click + Add Product to add a line.
                </div>
              )}

              {orderItems.length === 0 && isCompleteOrderItemDraft(newOrderItem) && (
                <Alert variant="info" className="mb-3 py-2">
                  This line will be included when you {editingOrder ? 'update' : 'create'} the order. Use + Add Product if you need more than one line.
                </Alert>
              )}

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3 border rounded mb-1" style={{ backgroundColor: 'var(--bs-secondary-bg)' }}>
                <span className="text-muted">Order total</span>
                <strong className="fs-5">{formatCurrency(calculateOrderTotal())}</strong>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseOrderModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={!hasAtLeastOneOrderLine()}>
              {editingOrder ? 'Update' : 'Create'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Sale Bill Modal */}
      <Modal show={showSaleBillModal} onHide={handleCloseSaleBillModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{editingSaleBill ? `Edit Sale Bill ${editingSaleBill.billNumber}` : 'Create Sale Bill'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Sale Order (optional)</Form.Label>
                <Form.Select
                  value={saleBillFormData.salesOrderId}
                  onChange={(e) => handleSaleOrderSelectForBill(e.target.value)}
                  disabled={!!editingSaleBill}
                >
                  <option value="">No sale order (standalone sale)</option>
                  {toArray(orders)
                    .filter((o) => o.orderStatus !== 'CANCELLED')
                    .map((o) => (
                      <option key={o.id} value={o.id}>{o.orderNumber} - {formatCustomerDisplayName(o.customer)}</option>
                    ))}
                </Form.Select>
                <Form.Text className="text-muted">Selecting one pre-fills the customer and fulfillment location</Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Customer *</Form.Label>
                <BillCustomerPicker
                  mode={billCustomerMode}
                  onModeChange={handleBillCustomerModeChange}
                  customers={toArray(customers)}
                  value={saleBillFormData.customerId}
                  onChange={(customerId) => setSaleBillFormData((prev) => ({ ...prev, customerId }))}
                  onCreated={(customer) => {
                    setCustomers((prev) => [...toArray(prev), customer]);
                    setSaleBillFormData((prev) => ({ ...prev, customerId: String(customer.id) }));
                  }}
                  disabled={!!editingSaleBill && editingSaleBill.status !== 'DRAFT'}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Bill Date *</Form.Label>
                <DateInput
                 
                  value={saleBillFormData.billDate}
                  onChange={(e) => setSaleBillFormData({ ...saleBillFormData, billDate: e.target.value })}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            {outboundMovementTypes.length === 0 && (
              <Col md={4}>
                <Alert variant="warning" className="py-2 small">
                  No Sale Bill movement type is set up. Ask a System Admin to add one under Goods Movement
                  Types before issuing bills.
                </Alert>
              </Col>
            )}
            {outboundMovementTypes.length > 1 && (
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Movement Type *</Form.Label>
                <Form.Select
                  value={saleBillFormData.movementTypeId}
                  onChange={(e) => setSaleBillFormData({ ...saleBillFormData, movementTypeId: e.target.value })}
                  required
                >
                  <option value="">Select Movement Type</option>
                  {outboundMovementTypes.map((mt) => (
                    <option key={mt.id} value={mt.id}>{mt.name}</option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">Only Sale Bill movement types (System Admin → Goods Movement Types)</Form.Text>
              </Form.Group>
            </Col>
            )}
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Issuing Location *</Form.Label>
                <LocationSelect
                  warehouses={warehouses}
                  retailStores={retailStores}
                  productionUnits={productionUnits}
                  restrictToType={(() => {
                    const mt = outboundMovementTypes.find((m) => String(m.id) === String(saleBillFormData.movementTypeId));
                    return mt?.locationType || null;
                  })()}
                  value={
                    saleBillFormData.issuingLocationType && saleBillFormData.issuingLocationId
                      ? `${saleBillFormData.issuingLocationType}:${saleBillFormData.issuingLocationId}`
                      : ''
                  }
                  onChange={(e) => handleIssuingLocationChangeForBill(e.target.value)}
                />
                <Form.Text className="text-muted">Where materials are being issued from</Form.Text>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Payment Method</Form.Label>
                <Form.Control
                  type="text"
                  value={saleBillFormData.paymentMethod}
                  onChange={(e) => setSaleBillFormData({ ...saleBillFormData, paymentMethod: e.target.value })}
                  placeholder="Cash, UPI, etc."
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group className="mb-3">
                <Form.Label>Due Date</Form.Label>
                <DateInput
                 
                  value={saleBillFormData.dueDate}
                  min={saleBillFormData.billDate}
                  onChange={(e) => setSaleBillFormData({ ...saleBillFormData, dueDate: e.target.value })}
                />
                <Form.Text muted>Blank = customer's credit days</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <h5 className="mt-3 mb-2">Line Items</h5>
          <Table bordered size="sm">
            <thead>
              <tr>
                <th>Product</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Discount %</th>
                <th>Tax Rate</th>
                <th>Lot</th>
                <th>CGST</th>
                <th>SGST/IGST</th>
                <th>Line Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {saleBillLineItems.map((line, index) => {
                const tax = calculateSaleBillLineTax(line);
                const interState = isSaleBillInterState();
                return (
                  <tr key={index}>
                    <td style={{ minWidth: 160 }}>
                      <Form.Select
                        size="sm"
                        value={line.productId}
                        onChange={(e) => handleSaleBillLineChange(index, 'productId', e.target.value)}
                      >
                        <option value="">Select Product</option>
                        {toArray(products).filter((p) => p.isActive !== false).map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td style={{ minWidth: 80 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        step="0.001"
                        value={line.quantity}
                        onChange={(e) => handleSaleBillLineChange(index, 'quantity', e.target.value)}
                      />
                    </td>
                    <td style={{ minWidth: 90 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.unitPrice}
                        onChange={(e) => handleSaleBillLineChange(index, 'unitPrice', e.target.value)}
                      />
                      <LastRateHint last={saleBillRates[line.productId]} current={line.unitPrice} onUse={() => applyLastSaleBillRate(index)} />
                    </td>
                    <td style={{ minWidth: 80 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        min="0"
                        step="0.01"
                        value={line.discountPercentage}
                        onChange={(e) => handleSaleBillLineChange(index, 'discountPercentage', e.target.value)}
                      />
                    </td>
                    <td style={{ minWidth: 110 }}>
                      <Form.Select
                        size="sm"
                        value={line.taxRateId}
                        onChange={(e) => handleSaleBillLineChange(index, 'taxRateId', e.target.value)}
                      >
                        <option value="">None</option>
                        {taxRates.map((t) => (
                          <option key={t.id} value={t.id}>{t.name}</option>
                        ))}
                      </Form.Select>
                    </td>
                    <td style={{ minWidth: 140 }}>
                      <Form.Select
                        size="sm"
                        value={line.grnLineItemId}
                        onChange={(e) => handleSaleBillLineChange(index, 'grnLineItemId', e.target.value)}
                        disabled={!line.productId}
                      >
                        <option value="">Select Lot</option>
                        {(line.lotOptions || []).map((lot) => (
                          <option key={lot.id} value={lot.id}>
                            {(lot.internalLotNumber || lot.id)}{lot.availableQuantity != null ? ` (${lot.availableQuantity} ${lot.uomSymbol || ''})` : ''}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td className="text-end">{formatCurrency(tax.cgst)}</td>
                    <td className="text-end">{formatCurrency(interState ? tax.igst : tax.sgst)}</td>
                    <td className="text-end">{formatCurrency(tax.lineTotal)}</td>
                    <td>
                      <Button size="sm" variant="outline-danger" onClick={() => handleRemoveSaleBillLine(index)}>×</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Button size="sm" variant="outline-primary" onClick={handleAddSaleBillLine} className="mb-3">
            + Add Line
          </Button>

          {saleBillLineItems.length > 0 && (() => {
            const totals = calculateSaleBillTotals();
            const interState = isSaleBillInterState();
            return (
              <div className="d-flex justify-content-end mb-3">
                <Table borderless size="sm" style={{ width: 320 }}>
                  <tbody>
                    <tr><td>Subtotal</td><td className="text-end">{formatCurrency(totals.subtotal)}</td></tr>
                    <tr><td>Discount</td><td className="text-end">-{formatCurrency(totals.discount)}</td></tr>
                    <tr><td>Taxable Amount</td><td className="text-end">{formatCurrency(totals.taxable)}</td></tr>
                    {interState ? (
                      <tr><td>IGST</td><td className="text-end">{formatCurrency(totals.igst)}</td></tr>
                    ) : (
                      <>
                        <tr><td>CGST</td><td className="text-end">{formatCurrency(totals.cgst)}</td></tr>
                        <tr><td>SGST</td><td className="text-end">{formatCurrency(totals.sgst)}</td></tr>
                      </>
                    )}
                    <tr className="fw-bold border-top"><td>Total</td><td className="text-end">{formatCurrency(totals.total)}</td></tr>
                  </tbody>
                </Table>
              </div>
            );
          })()}

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={saleBillFormData.notes}
              onChange={(e) => setSaleBillFormData({ ...saleBillFormData, notes: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSaleBillModal}>
            Cancel
          </Button>
          <Button variant="outline-primary" onClick={() => handleSaveSaleBill('DRAFT')}>
            Save as Draft
          </Button>
          <Button variant="primary" onClick={() => handleSaveSaleBill('ISSUED')}>
            Issue Bill
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Help Pane */}
    </Container>
  );
}

export default Sales;

